"use client"

import type React from "react"
import { useEffect, useRef } from "react"

interface MatrixRainProps {
    className?: string
}

export const MatrixRain: React.FC<MatrixRainProps> = ({ className = "" }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext("2d")
        if (!ctx) return

        // Set canvas dimensions to match parent
        const resizeCanvas = () => {
            if (canvas.parentElement) {
                canvas.width = canvas.parentElement.offsetWidth
                canvas.height = canvas.parentElement.offsetHeight
            }
        }

        resizeCanvas()
        window.addEventListener("resize", resizeCanvas)

        // Matrix rain characters (using cybersecurity-related characters)
        const chars = "01Secure&More"
        const charArray = chars.split("")

        const fontSize = 14
        const columns = canvas.width / fontSize

        // Array to track the y position of each column
        const drops: number[] = []
        for (let i = 0; i < columns; i++) {
            drops[i] = Math.floor(Math.random() * -100) // Random starting position above the canvas
        }

        // Drawing the characters
        const draw = () => {
            // Semi-transparent black to create fade effect
            ctx.fillStyle = "rgba(0, 0, 0, 0.05)"
            ctx.fillRect(0, 0, canvas.width, canvas.height)

            // Set the color and font
            ctx.fillStyle = "#ff0000" // Red color for matrix effect
            ctx.font = `${fontSize}px monospace`

            // Loop through drops
            for (let i = 0; i < drops.length; i++) {
                // Random character
                const char = charArray[Math.floor(Math.random() * charArray.length)]

                // x coordinate of the drop
                const x = i * fontSize

                // y coordinate of the drop
                const y = drops[i] * fontSize

                // Vary the opacity to create depth
                const opacity = Math.random() * 0.5 + 0.5
                ctx.fillStyle = `rgba(255, 0, 0, ${opacity})`

                // Draw the character
                ctx.fillText(char, x, y)

                // Reset when drop reaches bottom or randomly
                if (y > canvas.height && Math.random() > 0.975) {
                    drops[i] = 0
                }

                // Move the drop down
                drops[i]++
            }
        }

        // Animation loop
        const interval = setInterval(draw, 50)

        return () => {
            clearInterval(interval)
            window.removeEventListener("resize", resizeCanvas)
        }
    }, [])

    return <canvas ref={canvasRef} className={`absolute inset-0 z-0 ${className}`} />
}
